import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Factor, Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { parseSectionAccess, type Role, type SectionAccess } from '@/lib/permissions';

export interface AuthProfile {
  id: string;
  organization_id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: Role;
  is_active: boolean;
  section_access: SectionAccess;
}

/**
 * Authenticator Assurance Level — RFC 8485 / NIST SP 800-63B. Supabase
 * Auth issues `aal1` for password / OAuth sign-ins and upgrades to `aal2`
 * after a verified MFA challenge. The financial-data RLS policies require
 * `currentLevel === 'aal2'`.
 */
export type AuthAal = 'aal1' | 'aal2' | null;

export interface AuthMfaState {
  /** AAL of the *currently active* JWT (what RLS sees). */
  currentLevel: AuthAal;
  /**
   * AAL the session *could* reach if the user completes a challenge —
   * `aal2` here means "this user has a verified factor enrolled, but
   * hasn't satisfied the challenge in this session yet". `aal1` means
   * "no verified factor at all" (i.e. needs to enrol).
   */
  nextLevel: AuthAal;
  /** All factors on the auth.user, regardless of verified state. */
  factors: Factor[];
  /** Convenience: factors with status === 'verified' and factor_type === 'phone'. */
  verifiedPhoneFactors: Factor[];
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: AuthProfile | null;
  role: Role | null;
  loading: boolean;
  mfa: AuthMfaState;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** Re-pull factors + AAL from supabase-js. Call after enroll/challenge/verify. */
  refreshMfa: () => Promise<void>;
}

const EMPTY_MFA: AuthMfaState = {
  currentLevel: null,
  nextLevel: null,
  factors: [],
  verifiedPhoneFactors: [],
};

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * supabase-js re-announces the *same* session far more often than people
 * actually sign in: every time a background tab becomes visible again it
 * re-reads the session from storage and emits `SIGNED_IN`, and the token
 * auto-refresh ticker emits `TOKEN_REFRESHED` roughly once an hour. The
 * state setters below therefore keep the previous object whenever nothing
 * meaningful changed, so those events don't ripple a new `session` /
 * `profile` / `mfa` identity through every `useAuth()` consumer.
 */
function isSameSession(a: Session | null, b: Session | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.access_token === b.access_token &&
    a.refresh_token === b.refresh_token &&
    a.expires_at === b.expires_at &&
    a.user?.id === b.user?.id &&
    a.user?.email === b.user?.email &&
    a.user?.updated_at === b.user?.updated_at
  );
}

function isSameProfile(a: AuthProfile | null, b: AuthProfile | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.organization_id === b.organization_id &&
    a.email === b.email &&
    a.full_name === b.full_name &&
    a.avatar_url === b.avatar_url &&
    a.role === b.role &&
    a.is_active === b.is_active &&
    JSON.stringify(a.section_access) === JSON.stringify(b.section_access)
  );
}

function factorSignature(factors: Factor[]): string {
  return factors
    .map((f) => `${f.id}:${f.factor_type}:${f.status}:${f.friendly_name ?? ''}:${f.updated_at}`)
    .sort()
    .join('|');
}

function isSameMfa(a: AuthMfaState, b: AuthMfaState): boolean {
  if (a === b) return true;
  return (
    a.currentLevel === b.currentLevel &&
    a.nextLevel === b.nextLevel &&
    factorSignature(a.factors) === factorSignature(b.factors) &&
    factorSignature(a.verifiedPhoneFactors) === factorSignature(b.verifiedPhoneFactors)
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfa, setMfa] = useState<AuthMfaState>(EMPTY_MFA);

  // The user id behind the most recent auth event (or null when signed
  // out). A profile fetch that resolves after the user has changed or
  // signed out is stale and must not be applied.
  const activeUserIdRef = useRef<string | null>(null);
  // The user id whose profile fetch has already settled at least once.
  // This is what lets the auth listener tell a genuine sign-in (block the
  // UI until we know the role) apart from supabase-js re-announcing the
  // session we already have (refresh quietly in the background).
  const settledUserIdRef = useRef<string | null>(null);

  /**
   * Re-pull MFA state from supabase-js. The AAL comes from the cached JWT;
   * the factor list is a `GET /user` round-trip. Safe to call after every
   * enrol/challenge/verify mutation and on each auth event.
   *
   * IMPORTANT: never call this from inside `onAuthStateChange` directly —
   * it would re-enter supabase-js while the auth lock is held. Defer with
   * `setTimeout(..., 0)` like loadProfile does.
   */
  const refreshMfa = useCallback(async () => {
    try {
      const [{ data: aalData, error: aalError }, { data: factorData, error: factorError }] =
        await Promise.all([
          supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
          supabase.auth.mfa.listFactors(),
        ]);
      if (aalError) console.warn('[AuthProvider] getAAL failed', aalError);
      if (factorError) console.warn('[AuthProvider] listFactors failed', factorError);

      const all = factorData?.all ?? [];
      const phone = factorData?.phone ?? [];
      const verifiedPhone = phone.filter((f) => f.status === 'verified');
      // `listFactors()` is a network round-trip (GET /user). When a
      // background refresh fails — typically a tab waking up before the
      // network does — keep what we already know rather than reporting
      // "no factors", which would make RequireMfa yank a super_admin off a
      // financials page and into /auth/mfa/setup mid-task.
      setMfa((prev) => {
        const next: AuthMfaState = {
          currentLevel: aalError ? prev.currentLevel : ((aalData?.currentLevel as AuthAal) ?? null),
          nextLevel: aalError ? prev.nextLevel : ((aalData?.nextLevel as AuthAal) ?? null),
          factors: factorError ? prev.factors : all,
          verifiedPhoneFactors: factorError ? prev.verifiedPhoneFactors : verifiedPhone,
        };
        return isSameMfa(prev, next) ? prev : next;
      });
    } catch (err) {
      console.error('[AuthProvider] refreshMfa threw', err);
    }
  }, []);

  async function loadProfile(userId: string) {
    console.log('[AuthProvider] loadProfile start', { userId });
    // Ignore the result if the user signed out / switched accounts while
    // this request was in flight.
    const isStale = () => activeUserIdRef.current !== userId;
    // A failed *re*-fetch keeps the profile we already have for this user.
    // Dropping it would send someone mid-form to /auth/pending because
    // their laptop woke up a second before the Wi-Fi did.
    const keepExistingProfile = () =>
      setProfile((prev) => (prev && prev.id === userId ? prev : null));
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, organization_id, email, full_name, avatar_url, role, is_active, section_access')
        .eq('id', userId)
        .maybeSingle();
      if (isStale()) return;
      if (error) {
        // Most common in fresh prod environments: migrations haven't been
        // applied yet, so `public.profiles` doesn't exist. Surface a clear
        // message so we don't silently spin on a loading screen.
        console.error('[AuthProvider] Failed to load profile', error);
        keepExistingProfile();
        return;
      }
      console.log('[AuthProvider] loadProfile ok', {
        found: !!data,
        role: data?.role,
        is_active: data?.is_active,
      });
      const next: AuthProfile | null = data
        ? {
            ...data,
            section_access: parseSectionAccess(data.section_access),
          }
        : null;
      setProfile((prev) => (isSameProfile(prev, next) ? prev : next));
    } catch (err) {
      // Network/CORS failures and missing-table 4xx in some supabase-js
      // versions throw rather than returning `{ error }`. Same handling as
      // the `{ error }` branch so `loading` never gets stuck.
      if (isStale()) return;
      console.error('[AuthProvider] loadProfile threw', err);
      keepExistingProfile();
    } finally {
      if (!isStale()) {
        settledUserIdRef.current = userId;
        console.log('[AuthProvider] loadProfile finally: setLoading(false)');
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        console.log('[AuthProvider] init: getSession start');
        const { data } = await supabase.auth.getSession();
        console.log('[AuthProvider] init: getSession ok', {
          hasSession: !!data.session,
          email: data.session?.user?.email,
        });
        if (!mounted) return;
        setSession((prev) => (isSameSession(prev, data.session) ? prev : data.session));
        if (data.session?.user) {
          activeUserIdRef.current = data.session.user.id;
          // Pull MFA state in parallel with the profile fetch so the
          // RequireMfa gate has a definitive answer before we render any
          // financials route.
          await Promise.all([loadProfile(data.session.user.id), refreshMfa()]);
        } else {
          setMfa(EMPTY_MFA);
          // No session — auth state is settled.
          setLoading(false);
        }
      } catch (err) {
        console.error('[AuthProvider] getSession failed', err);
        if (mounted) setLoading(false);
      }
    })();

    // IMPORTANT: this callback must NOT be async and must NOT directly call
    // supabase methods. supabase-js holds an internal auth lock while
    // dispatching auth events, and any Supabase request made from here would
    // need the same lock — producing a deadlock that hangs both this callback
    // and whatever triggered the event (e.g. `exchangeCodeForSession`).
    // See: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
    const { data: subscription } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!mounted) return;
      setSession((prev) => (isSameSession(prev, newSession) ? prev : newSession));
      if (!newSession?.user) {
        console.log('[AuthProvider] onAuthStateChange', { event, hasSession: false });
        activeUserIdRef.current = null;
        settledUserIdRef.current = null;
        setProfile(null);
        setMfa(EMPTY_MFA);
        setLoading(false);
        return;
      }

      const userId = newSession.user.id;
      activeUserIdRef.current = userId;

      // Only a *different* user than the one we already loaded is a real
      // sign-in. For that case keep `loading=true` until the profile fetch
      // resolves so RequireAuth shows the loading screen instead of
      // bouncing to /auth/pending in the gap between session and profile.
      //
      // Everything else — the `SIGNED_IN` supabase-js re-emits whenever a
      // background tab becomes visible again, the hourly `TOKEN_REFRESHED`,
      // `USER_UPDATED`, `MFA_CHALLENGE_VERIFIED` — is the same person we
      // already know about. Those refresh profile + MFA state quietly in
      // the background WITHOUT touching `loading`. Flipping it used to swap
      // the whole app for <LoadingScreen /> for a moment, which unmounted
      // every page and threw away any open dialog, half-filled form, or
      // in-progress MFA code the instant someone glanced at another tab.
      const isSameUser = settledUserIdRef.current === userId;
      console.log('[AuthProvider] onAuthStateChange', {
        event,
        hasSession: true,
        email: newSession.user.email,
        mode: isSameUser ? 'background-refresh' : 'blocking-load',
      });
      if (!isSameUser) setLoading(true);

      // Defer the profile + MFA fetches to the next tick so we're outside
      // the auth lock by the time we make Supabase calls (avoids the
      // supabase-js deadlock described above). MFA_CHALLENGE_VERIFIED
      // events fire here too, which is exactly when we need to refresh
      // factors + AAL so the gate flips from "challenge" to "allow".
      setTimeout(() => {
        if (!mounted) return;
        void loadProfile(userId);
        void refreshMfa();
      }, 0);
    });

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
    };
    // refreshMfa is a stable useCallback (empty deps), included so the
    // exhaustive-deps lint stays happy without triggering re-subscribes.
  }, [refreshMfa]);

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: {
          access_type: 'offline',
          prompt: 'select_account',
        },
      },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setMfa(EMPTY_MFA);
  };

  const refreshProfile = async () => {
    if (session?.user) await loadProfile(session.user.id);
  };

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    profile,
    role: profile?.role ?? null,
    loading,
    mfa,
    signInWithGoogle,
    signOut,
    refreshProfile,
    refreshMfa,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Like `useAuth`, but returns null outside an `AuthProvider` instead of
 *  throwing. For shared primitives (e.g. `DataTable`) that can render in
 *  isolation but enhance their behaviour when a user is signed in. */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
